// Sign-in and load errors built on @union/ui (DESIGN.md); import from "@union/web-kit/ui".

import { Button } from "@union/ui/components/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@union/ui/components/field";
import { Input } from "@union/ui/components/input";
import { AuthLayout } from "@union/ui/layouts/auth-layout";
import { cn } from "@union/ui/lib/utils";
import { CircleAlert, KeyRound } from "lucide-react";
import { type FormEvent, useState } from "react";
import { ApiRequestError, ApiUnreachableError, signOut, supabase } from "./index.tsx";

/** Email and password (and a passkey where the site allows it), on the sign-in layout. */
export function SignInPage({ product, description }: { product: string; description?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) setError("Those details didn’t work. Check them and try again.");
  }

  // Passkeys work on the sign-in site, Staff and Ops (the relying party's listed origins).
  async function passkey() {
    setError(null);
    const { error } = await supabase.auth.signInWithPasskey();
    if (error) {
      setError(
        error.name === "NotAllowedError" || /cancel|abort/i.test(error.message)
          ? "Passkey sign-in was cancelled."
          : "That passkey didn’t work here. Sign in with your password, then add a passkey.",
      );
    }
  }

  const passkeys = "PublicKeyCredential" in window && import.meta.env.VITE_PASSKEYS !== "off";
  return (
    <AuthLayout
      eyebrow="Union Clubs"
      title={product}
      description={description}
      footer={
        import.meta.env.DEV
          ? "Local: jordan@, riley@, morgan@, casey@ or sam@example.com / union-local-only"
          : "Trouble signing in? Ask your club’s pro shop."
      }
    >
      <form className="grid gap-space-4" onSubmit={submit}>
        <FieldGroup className="gap-space-4">
          <Field>
            <FieldLabel htmlFor="signin-email">Email</FieldLabel>
            <Input
              id="signin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field data-invalid={!!error}>
            <FieldLabel htmlFor="signin-password">Password</FieldLabel>
            <Input
              id="signin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!error}
              required
            />
            <FieldError>{error}</FieldError>
          </Field>
        </FieldGroup>
        <Button type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        {passkeys && (
          <Button type="button" variant="outline" onClick={passkey}>
            <KeyRound /> Sign in with a passkey
          </Button>
        )}
      </form>
    </AuthLayout>
  );
}

/**
 * What went wrong loading something, in words staff or members can act on, with Try again.
 * Server errors show a short reference to quote to support.
 */
export function ErrorState({
  what,
  error,
  onRetry,
  onSignIn = signOut,
  className,
}: {
  what: string;
  error: unknown;
  onRetry: () => void;
  /** What "Sign in again" does; defaults to signing out of Supabase Auth. */
  onSignIn?: () => void;
  className?: string;
}) {
  const expired = error instanceof ApiRequestError && error.status === 401;
  const reason = expired
    ? "Your session ended."
    : error instanceof ApiUnreachableError
      ? "Can’t reach Union. Check your connection."
      : `Couldn’t load ${what}. Something went wrong on our end.`;
  const ref = error instanceof ApiRequestError && error.requestId ? error.requestId.slice(0, 8) : null;
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-wrap items-center gap-space-2 rounded-md border border-destructive/30 bg-destructive-soft px-space-4 py-space-3 type-body text-destructive",
        className,
      )}
    >
      <CircleAlert aria-hidden="true" className="size-4 shrink-0" />
      <span className="flex-1">
        {reason}
        {ref && !expired && <span className="type-caption"> (ref {ref})</span>}
      </span>
      <Button variant="outline" size="sm" onClick={() => (expired ? onSignIn() : onRetry())}>
        {expired ? "Sign in again" : "Try again"}
      </Button>
    </div>
  );
}
